export type FacebookDispatchStyle = 'default' | 'serverAttribution' | 'ifPixelFires';

export interface FacebookEventMapping {
  metaName: string;
  dispatchStyle?: FacebookDispatchStyle;
}

/** Domain / WebEngage event name → Meta event. Unlisted names are no-ops. */
export const FACEBOOK_EVENT_MAP: Record<string, FacebookEventMapping> = {
  PageView: { metaName: 'PageView' },
  'PDP Page Viewed': { metaName: 'ViewContent', dispatchStyle: 'serverAttribution' },
  ViewContent: { metaName: 'ViewContent', dispatchStyle: 'serverAttribution' },
  'Added To Cart': { metaName: 'AddToCart' },
  AddToCart: { metaName: 'AddToCart' },
  'Added To Wishlist': { metaName: 'AddToWishlist' },
  'Store-Visit Item Added to Wishlist': { metaName: 'AddToWishlist' },
  AddToWishlist: { metaName: 'AddToWishlist' },
  'Product Listing Page Viewed': { metaName: 'PLP Viewed' },
  'PLP Viewed': { metaName: 'PLP Viewed' },
  CustomizeProduct: { metaName: 'CustomizeProduct' },
  Search: { metaName: 'Search' },
  CompleteRegistration: { metaName: 'CompleteRegistration' },
  FindLocation: { metaName: 'FindLocation', dispatchStyle: 'ifPixelFires' },
  'Pincode Checked': { metaName: 'DeliveryAvailabilityChecked' },
  DeliveryAvailabilityChecked: { metaName: 'DeliveryAvailabilityChecked' },
  'Available Stores Clicked': { metaName: 'StoreInventoryViewed' },
  StoreInventoryViewed: { metaName: 'StoreInventoryViewed' },
  'PDP Video Played': { metaName: 'PDP Video Played' },
  RecentlyViewedOpened: { metaName: 'RecentlyViewedOpened' },
};

export type OpenAiStandardEventName =
  | 'page_viewed'
  | 'contents_viewed'
  | 'items_added'
  | 'registration_completed';

export interface OpenAiEventMapping {
  /** One of OpenAI's standard events, or 'custom' with `customEventName` set. */
  oaiName: OpenAiStandardEventName | 'custom';
  customEventName?: string;
}

/**
 * Domain / WebEngage event name → OpenAI Ads Measurement Pixel event.
 * Mirrors the same event surface as `FACEBOOK_EVENT_MAP` so the two ad
 * pixels measure the same set of conversions. Unlisted names are no-ops.
 */
export const OPENAI_EVENT_MAP: Record<string, OpenAiEventMapping> = {
  PageView: { oaiName: 'page_viewed' },
  'PDP Page Viewed': { oaiName: 'contents_viewed' },
  ViewContent: { oaiName: 'contents_viewed' },
  'Added To Cart': { oaiName: 'items_added' },
  AddToCart: { oaiName: 'items_added' },
  'Added To Wishlist': { oaiName: 'custom', customEventName: 'add_to_wishlist' },
  'Store-Visit Item Added to Wishlist': { oaiName: 'custom', customEventName: 'add_to_wishlist' },
  AddToWishlist: { oaiName: 'custom', customEventName: 'add_to_wishlist' },
  'Product Listing Page Viewed': { oaiName: 'contents_viewed' },
  'PLP Viewed': { oaiName: 'contents_viewed' },
  CustomizeProduct: { oaiName: 'custom', customEventName: 'customize_product' },
  Search: { oaiName: 'custom', customEventName: 'search' },
  CompleteRegistration: { oaiName: 'registration_completed' },
  FindLocation: { oaiName: 'custom', customEventName: 'find_location' },
  'Pincode Checked': { oaiName: 'custom', customEventName: 'delivery_availability_checked' },
  DeliveryAvailabilityChecked: { oaiName: 'custom', customEventName: 'delivery_availability_checked' },
  'Available Stores Clicked': { oaiName: 'custom', customEventName: 'store_inventory_viewed' },
  StoreInventoryViewed: { oaiName: 'custom', customEventName: 'store_inventory_viewed' },
  'PDP Video Played': { oaiName: 'custom', customEventName: 'pdp_video_played' },
  RecentlyViewedOpened: { oaiName: 'custom', customEventName: 'recently_viewed_opened' },
};
